import imageCompression from 'browser-image-compression'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Combobox,
  EInputTypes,
  ETypeOfNotification,
  initialStatePhoneNumber,
  InputDate,
  InputPhone,
  InputSwitch,
  InputTextCustom,
  mobilePhoneToString,
  PhoneNumber,
  setNotification,
  validMobileForInputPhone,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
import { RadioButton } from 'primereact/radiobutton'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { useDispatch } from 'react-redux'
import { Modal } from 'medere-ui-framework'
import { defaultComboDataDTO } from '../../dto/ComboDataDTO'
import {
  defaultUsersFormDTO,
  UsersConversionCandidateDTO,
  UsersFormDTO,
  UsersHealthEntityOptionDTO,
  UsersProfileOptionDTO,
  } from '../../dto/users/UsersDTO'
import {
  buildUsersSavePayload,
  findUsersConversionCandidates,
  getUserById,
  getUserCountries,
  getUserDefaultLocationContext,
  getUserDocumentTypes,
  getUserHealthEntities,
  getUserProfiles,
  getUserProvinces,
  getUserCitiesByProvince,
  loadUserSignature,
  getUserSignatureBlob,
  saveUser,
  deleteUserSignature,
  uploadUserSignature,
} from '../../servicesHooks/Users/useUsersService'
import { isValidEmail } from '../../utils/utils'
import './Users.scss'

interface UsersFormErrors {
  firstname?: string
  lastname?: string
  email?: string
  username?: string
  profile?: string
  password?: string
  confirmPassword?: string
  documentType?: string
  document?: string
  gender?: string
  birthDate?: string
  city?: string
  address?: string
  healthEntities?: string
}

interface UsersFormViewProps {
  userId: number
  mode: 'edit' | 'view'
  setUserId: (userId: number | null) => void
  setMode: (mode: 'edit' | 'view') => void
  onSaved?: () => void
}

interface GenderOption {
  id: string
  name: string
}

const genderOptions: GenderOption[] = [
  { id: 'M', name: 'Masculino' },
  { id: 'F', name: 'Femenino' },
]

const imageCompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
}

function hasAuditorTag(profile: UsersProfileOptionDTO | null | undefined) {
  return Array.isArray(profile?.tagCodes) && profile.tagCodes.some((tag) => tag === 'AUDITOR')
}

function normalizeText(value: string | null | undefined) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function normalizeDateValue(value: Date | null | undefined) {
  return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null
}

function formatConversionDocument(candidate: UsersConversionCandidateDTO) {
  const documentType = candidate.documentType.trim() || 'DNI'
  const document = candidate.document.trim()
  return document === '' ? documentType : `${documentType}: ${document}`
}

function getConversionCandidateTypeLabel(candidate: UsersConversionCandidateDTO) {
  return candidate.onlyPatient === false ? 'Administrativo' : 'Paciente'
}

export default function UsersFormView({
  userId,
  mode,
  setUserId,
  setMode,
  onSaved,
}: Readonly<UsersFormViewProps>) {
  const dispatch = useDispatch()
  const [form, setForm] = useState<UsersFormDTO>(defaultUsersFormDTO)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<UsersFormErrors>({})
  const [loading, setLoading] = useState(userId > 0)
  const [saving, setSaving] = useState(false)
  const [profileOptions, setProfileOptions] = useState<UsersProfileOptionDTO[]>([])
  const [healthEntityOptions, setHealthEntityOptions] = useState<UsersHealthEntityOptionDTO[]>([])
  const [selectedHealthEntities, setSelectedHealthEntities] = useState<UsersHealthEntityOptionDTO[]>([])
  const [mobilePhone, setMobilePhoneState] = useState<PhoneNumber>(initialStatePhoneNumber)
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null)
  const [pendingSignatureFile, setPendingSignatureFile] = useState<File | null>(null)
  const [pendingSignatureDeletion, setPendingSignatureDeletion] = useState(false)
  const [loadingSignature, setLoadingSignature] = useState(false)
  const [conversionCandidates, setConversionCandidates] = useState<UsersConversionCandidateDTO[]>([])
  const [selectedConversionCandidateId, setSelectedConversionCandidateId] = useState<number | null>(null)
  const [conversionModalVisible, setConversionModalVisible] = useState(false)
  const [conversionSearchLoading, setConversionSearchLoading] = useState(false)
  const lastConversionLookupKeyRef = useRef('')
  const conversionSearchPendingRef = useRef(false)

  const isEditMode = userId > 0
  const readOnly = mode === 'view'
  const selectedProfile = form.profile?.id != null && form.profile.id > 0
    ? profileOptions.find((item) => item.id === form.profile?.id) ?? null
    : null
  const isAuditorProfile = hasAuditorTag(selectedProfile)
  const availableHealthEntityOptions = useMemo(() => {
    const addedIds = new Set(form.healthEntities.map((item) => item.id))
    return healthEntityOptions.filter((item) => !addedIds.has(item.id))
  }, [form.healthEntities, healthEntityOptions])
  const title = useMemo(() => {
    if (readOnly) return 'Detalle de usuario'
    return isEditMode ? 'Editar usuario' : 'Crear usuario'
  }, [isEditMode, readOnly])
  const showConsentStatus = isEditMode
  const consentAccepted = form.consentSigningDate != null
  const selectedConversionCandidate = useMemo(
    () => conversionCandidates.find((candidate) => candidate.medereEntityId === selectedConversionCandidateId) ?? null,
    [conversionCandidates, selectedConversionCandidateId],
  )
  const conversionModalSubtitle = selectedConversionCandidate?.hasUser
    ? 'El número de documento ya está asociado a un perfil en el sistema. Podés seleccionar el perfil existente para editarlo, o continuar creando un perfil adicional utilizando este mismo número de documento.'
    : 'El número de documento ya está asociado a una persona en el sistema. Podés seleccionar el perfil para crear un usuario utilizando este mismo número de documento.'
  const conversionCreateButtonLabel = selectedConversionCandidate?.hasUser
    ? 'Crear perfil adicional'
    : 'Crear usuario'

  useEffect(() => {
    let cancelled = false

    async function loadProfiles() {
      try {
        const [items, healthEntities] = await Promise.all([
          getUserProfiles({ filterValue: '', offSet: 0, pageSize: 200 }),
          getUserHealthEntities({ filterValue: '', offSet: 0, pageSize: 500 }),
        ])
        if (!cancelled) {
          setProfileOptions(items)
          setHealthEntityOptions(healthEntities)
        }
      } catch (error) {
        console.error(error)
      }
    }

    void loadProfiles()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const nextPhoneRaw = (form.mobilePhone ?? '').trim()
    setMobilePhoneState((currentPhone) => {
      const currentPhoneRaw = mobilePhoneToString(currentPhone).trim()
      if (nextPhoneRaw === currentPhoneRaw) {
        return currentPhone
      }
      return nextPhoneRaw !== '' ? validMobileForInputPhone(nextPhoneRaw) : initialStatePhoneNumber
    })
  }, [form.mobilePhone])

  useEffect(() => {
    const input = document.querySelector<HTMLInputElement>('input[name="usersDocument"]')
    if (!input) {
      return undefined
    }

    const handleBlur = () => {
      void handleDocumentBlurConversionSearch()
    }

    input.addEventListener('blur', handleBlur)
    return () => {
      input.removeEventListener('blur', handleBlur)
    }
  }, [conversionModalVisible, conversionSearchLoading, form.document, form.documentType?.id, loading, readOnly, isEditMode])

  useEffect(() => {
    let cancelled = false

    async function loadUser() {
      if (userId <= 0) {
        setForm(defaultUsersFormDTO)
        setPassword('')
        setConfirmPassword('')
        setErrors({})
        setSelectedHealthEntities([])
        setSignaturePreview(null)
        setPendingSignatureFile(null)
        setPendingSignatureDeletion(false)
        setLoading(false)

        try {
          const [defaultLocation, documentTypes] = await Promise.all([
            getUserDefaultLocationContext(),
            getUserDocumentTypes({ filterValue: '', offSet: 0, pageSize: 200 }),
          ])
          const defaultDocumentType = documentTypes.find((item) => normalizeText(item.name) === 'dni') ?? null
          if (!cancelled) {
            setForm((current) => ({
              ...current,
              documentType: defaultDocumentType ?? current.documentType,
              country: defaultLocation.country ?? defaultComboDataDTO,
              province: defaultLocation.province ?? defaultComboDataDTO,
              city: defaultLocation.city ?? defaultComboDataDTO,
            }))
          }
        } catch (error) {
          console.error(error)
        }
        return
      }

        setLoading(true)
      try {
        const data = await getUserById(userId)
        if (!cancelled) {
          const currentProfileOption = data.profile?.id != null && data.profile.id > 0
            ? profileOptions.find((item) => item.id === data.profile?.id) ?? null
            : null
          setForm({
            ...defaultUsersFormDTO,
            ...data,
            profile: currentProfileOption ?? data.profile ?? defaultComboDataDTO,
            language: data.language ?? defaultComboDataDTO,
            documentType: data.documentType ?? defaultComboDataDTO,
            country: data.country ?? defaultComboDataDTO,
            province: data.province ?? defaultComboDataDTO,
            city: data.city ?? defaultComboDataDTO,
            healthEntities: data.healthEntities ?? [],
          })
          setSelectedHealthEntities([])
          setPendingSignatureFile(null)
          setPendingSignatureDeletion(false)
        }

        try {
          const signatureUrl = await loadUserSignature(userId)
          if (!cancelled) {
            setSignaturePreview(signatureUrl)
          }
        } catch (error) {
          if (!cancelled) {
            setSignaturePreview(null)
          }
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          dispatch(setNotification({
            title: 'Error',
            desc: 'No se pudieron cargar los datos del usuario.',
            typeOfNotification: ETypeOfNotification.ERROR,
            icon: 'pi pi-times',
          }))
          setUserId(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadUser()
    return () => {
      cancelled = true
    }
  }, [dispatch, setUserId, userId])

  function updateField<K extends keyof UsersFormDTO>(field: K, value: UsersFormDTO[K]) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleDocumentBlurConversionSearch() {
    if (readOnly || isEditMode || conversionModalVisible || conversionSearchLoading) {
      return
    }

    const documentTypeId = form.documentType?.id ?? null
    const documentValue = form.document.trim()
    if (!documentTypeId || documentTypeId <= 0 || documentValue === '') {
      return
    }

    const lookupKey = `${documentTypeId}:${documentValue}`
    if (lastConversionLookupKeyRef.current === lookupKey) {
      return
    }

    lastConversionLookupKeyRef.current = lookupKey
    setConversionSearchLoading(true)
    conversionSearchPendingRef.current = true
    try {
      const candidates = await findUsersConversionCandidates(documentTypeId, documentValue)
      if (candidates.length > 0) {
        setConversionCandidates(candidates)
        setSelectedConversionCandidateId(candidates[0]?.medereEntityId ?? null)
        setConversionModalVisible(true)
      }
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudieron buscar coincidencias por documento.',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      conversionSearchPendingRef.current = false
      setConversionSearchLoading(false)
    }
  }

  function closeConversionModal() {
    setConversionModalVisible(false)
    setConversionCandidates([])
    setSelectedConversionCandidateId(null)
  }

  function prefillConversionCandidate(candidate: UsersConversionCandidateDTO, options?: {
    clearUsername?: boolean
    clearProfile?: boolean
  }) {
    const clearUsername = options?.clearUsername ?? false
    const clearProfile = options?.clearProfile ?? false

    setMode('edit')
    setUserId(-1)
    setPassword('')
    setConfirmPassword('')

    setForm((current) => ({
      ...current,
      medereEntityId: candidate.medereEntityId,
      userId: -1,
      username: clearUsername ? '' : (candidate.username ?? current.username),
      firstname: candidate.firstname ?? current.firstname,
      lastname: candidate.lastname ?? current.lastname,
      document: candidate.document ?? current.document,
      email: candidate.email ?? current.email,
      gender: candidate.gender ? candidate.gender.trim().toUpperCase().charAt(0) : current.gender,
      birthDate: candidate.birthDate ?? null,
      address: candidate.address ?? current.address,
      mobilePhone: candidate.mobilePhone?.trim() ? candidate.mobilePhone : current.mobilePhone,
      country: candidate.country ?? current.country,
      province: candidate.province ?? current.province,
      city: candidate.city ?? current.city,
      profile: clearProfile ? defaultComboDataDTO : current.profile,
      blocked: clearProfile ? false : current.blocked,
      healthEntities: [],
    }))

    setSelectedHealthEntities([])
    setErrors((current) => ({
      ...current,
      username: undefined,
      profile: undefined,
      password: undefined,
      confirmPassword: undefined,
      healthEntities: undefined,
    }))
  }

  function applyConversionCandidate(candidate: UsersConversionCandidateDTO) {
    closeConversionModal()
    setErrors({})
    setPendingSignatureFile(null)
    setPendingSignatureDeletion(false)
    setSignaturePreview(null)

    if (candidate.hasUser && candidate.userId != null && candidate.userId > 0) {
      setMode('edit')
      setUserId(candidate.userId)
      return
    }

    prefillConversionCandidate(candidate)
  }

  async function applyConversionCandidateAsNewProfile(candidate: UsersConversionCandidateDTO) {
    closeConversionModal()
    setErrors({})
    setPendingSignatureFile(null)
    setPendingSignatureDeletion(false)
    setSignaturePreview(null)

    if (candidate.hasUser && candidate.userId != null && candidate.userId > 0) {
      try {
        const data = await getUserById(candidate.userId)

        let signatureFile: File | null = null
        let signatureUrl: string | null = null
        try {
          const signatureBlob = await getUserSignatureBlob(candidate.userId)
          if (signatureBlob.size > 0) {
            signatureFile = new File([signatureBlob], 'signature.png', { type: signatureBlob.type || 'image/png' })
            signatureUrl = URL.createObjectURL(signatureBlob)
          }
        } catch (error) {
          signatureFile = null
          signatureUrl = null
        }

        setMode('edit')
        setUserId(-1)
        setPassword('')
        setConfirmPassword('')
        setPendingSignatureFile(signatureFile)
        setPendingSignatureDeletion(false)
        setSignaturePreview(signatureUrl)
        setSelectedHealthEntities([])

        setForm({
          ...defaultUsersFormDTO,
          ...data,
          medereEntityId: candidate.medereEntityId,
          userId: -1,
          username: '',
          profile: defaultComboDataDTO,
          blocked: false,
          healthEntities: data.healthEntities ?? [],
          language: data.language ?? defaultComboDataDTO,
          documentType: data.documentType ?? defaultComboDataDTO,
          country: data.country ?? defaultComboDataDTO,
          province: data.province ?? defaultComboDataDTO,
          city: data.city ?? defaultComboDataDTO,
        })

        setErrors((current) => ({
          ...current,
          username: undefined,
          profile: undefined,
          password: undefined,
          confirmPassword: undefined,
          healthEntities: undefined,
        }))
        return
      } catch (error) {
        console.error(error)
      }
    }

    prefillConversionCandidate(candidate, {
      clearUsername: true,
      clearProfile: true,
    })
  }

  function addHealthEntity() {
    const validHealthEntities = selectedHealthEntities.filter((item) => item?.id && item.id > 0)
    if (validHealthEntities.length === 0) return

    setForm((current) => {
      const currentIds = new Set(current.healthEntities.map((item) => item.id))
      const entitiesToAdd = validHealthEntities.filter((item) => !currentIds.has(item.id))
      if (entitiesToAdd.length === 0) return current

      return {
        ...current,
        healthEntities: [...current.healthEntities, ...entitiesToAdd],
      }
    })

    setSelectedHealthEntities([])
    setErrors((current) => ({ ...current, healthEntities: undefined }))
  }

  function removeHealthEntity(healthEntityId: number | null) {
    if (!healthEntityId || healthEntityId <= 0 || readOnly) return

    setForm((current) => ({
      ...current,
      healthEntities: current.healthEntities.filter((item) => item.id !== healthEntityId),
    }))
  }

  function handleCountryChange(value: any) {
    updateField('country', value ?? defaultComboDataDTO)
    updateField('province', defaultComboDataDTO)
    updateField('city', defaultComboDataDTO)
    setErrors((current) => ({ ...current, city: undefined }))
  }

  function handleProvinceChange(value: any) {
    updateField('province', value ?? defaultComboDataDTO)
    updateField('city', defaultComboDataDTO)
    setErrors((current) => ({ ...current, city: undefined }))
  }

  function handleCityChange(value: any) {
    updateField('city', value ?? defaultComboDataDTO)
    setErrors((current) => ({ ...current, city: undefined }))
  }

  async function handleSignatureUpload(file?: File | null) {
    if (!file) return

    setLoadingSignature(true)
    try {
      const compressedFile = await imageCompression(file, imageCompressionOptions)
      const normalizedFile = compressedFile as File
      setPendingSignatureDeletion(false)

      if (userId > 0) {
        await uploadUserSignature(userId, normalizedFile)
        setPendingSignatureFile(null)
        setSignaturePreview(URL.createObjectURL(normalizedFile))
        dispatch(setNotification({
          title: 'Firma actualizada',
          desc: 'La foto de firma se guardo correctamente.',
          typeOfNotification: ETypeOfNotification.INFO,
          icon: 'pi pi-check',
        }))
      } else {
        setSignaturePreview(URL.createObjectURL(normalizedFile))
        setPendingSignatureFile(normalizedFile)
      }
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudo cargar la foto de firma.',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      setLoadingSignature(false)
    }
  }

  function handleRemoveSignature() {
    if (readOnly || loadingSignature) return

    setSignaturePreview(null)
    setPendingSignatureFile(null)
    setPendingSignatureDeletion(true)

    const input = document.getElementById('usersSignatureInput') as HTMLInputElement | null
    if (input) {
      input.value = ''
    }
  }

  function validate(): UsersFormErrors {
    const nextErrors: UsersFormErrors = {}
    const requiresPassword = !isEditMode
    const hasPasswordInput = password.trim() !== '' || confirmPassword.trim() !== ''

    if (!form.documentType?.id || form.documentType.id <= 0) nextErrors.documentType = 'El tipo de documento es obligatorio'
    if (form.document.trim() === '') nextErrors.document = 'El número de documento es obligatorio'
    if (form.firstname.trim() === '') nextErrors.firstname = 'El nombre es obligatorio'
    if (form.lastname.trim() === '') nextErrors.lastname = 'El apellido es obligatorio'
    if (!form.gender) nextErrors.gender = 'El sexo es obligatorio'

    if (form.email.trim() !== '' && !isValidEmail(form.email)) {
      nextErrors.email = 'Ingrese un mail válido'
    }

    if (form.username.trim() === '') nextErrors.username = 'El usuario es obligatorio'
    if (!form.profile?.id || form.profile.id <= 0) nextErrors.profile = 'El perfil es obligatorio'

    if (requiresPassword) {
      if (password.trim() === '') nextErrors.password = 'La contraseña es obligatoria'
      if (confirmPassword.trim() === '') nextErrors.confirmPassword = 'La confirmación es obligatoria'
    }

    if (hasPasswordInput && password !== confirmPassword) {
      nextErrors.confirmPassword = 'Las contraseñas no coinciden'
    }

    if (isAuditorProfile && form.healthEntities.length === 0) {
      nextErrors.healthEntities = 'Debe agregar al menos una cobertura médica'
    }

    return nextErrors
  }

  async function handleSave() {
    if (conversionSearchPendingRef.current || conversionSearchLoading || conversionModalVisible) {
      dispatch(setNotification({
        title: 'Validación en curso',
        desc: 'Espera a que termine la búsqueda de coincidencias antes de guardar.',
        typeOfNotification: ETypeOfNotification.WARNING,
        icon: 'pi pi-exclamation-triangle',
      }))
      return
    }

    const validationErrors = validate()
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      dispatch(setNotification({
        title: 'Validación',
        desc: Object.values(validationErrors)[0] ?? 'Complete los datos obligatorios',
        typeOfNotification: ETypeOfNotification.WARNING,
        icon: 'pi pi-exclamation-triangle',
      }))
      return
    }

    setSaving(true)
    try {
      const savedUserId = await saveUser(buildUsersSavePayload(form, password, confirmPassword))

      if (pendingSignatureFile) {
        await uploadUserSignature(savedUserId, pendingSignatureFile)
      }
      if (pendingSignatureDeletion) {
        await deleteUserSignature(savedUserId)
      }
      dispatch(setNotification({
        title: isEditMode ? 'Usuario actualizado' : 'Usuario creado',
        desc: isEditMode ? 'Los cambios se guardaron correctamente.' : 'El usuario se creó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      onSaved?.()
      setPassword('')
      setConfirmPassword('')
      setErrors({})
      setPendingSignatureFile(null)
      setPendingSignatureDeletion(false)

      setMode('view')
      setUserId(null)
    } catch (error: unknown) {
      const response = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response
        : undefined
      const status = typeof response?.status === 'number' ? response.status : null

      if (status === 409) {
        const duplicateMessage = 'Ese nombre de usuario ya está en uso.'
        setErrors((current) => ({ ...current, username: duplicateMessage }))
        dispatch(setNotification({
          title: 'No se pudo guardar',
          desc: duplicateMessage,
          typeOfNotification: ETypeOfNotification.WARNING,
          icon: 'pi pi-exclamation-triangle',
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudieron guardar los cambios del usuario.',
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='viewContainer users-entities-view users-entities-form-view'>
      <div className='users-entities-back-action'>
        <Button className='btnCancel' label='<< Volver' onClick={() => setUserId(null)} />
      </div>

      <div className='users-entities-header'>
        <h1 className='users-entities-title'>{title}</h1>

        {readOnly && (
          <Button
            type='button'
            className='buttonMain users-entities-edit-btn'
            label='Editar'
            icon='pi pi-file-edit'
            onClick={() => setMode('edit')}
          />
        )}
         
      </div>
      {showConsentStatus && (
      <div className='users-entities-consent-status'>
        {consentAccepted
          ? 'Consentimiento aceptado por el usuario'
          : 'Consentimiento pendiente de aceptación'}
      </div>
      )}
      <div className='users-entities-divider' />

      {loading ? (
        <div className='users-entities-loading'>Cargando datos del usuario...</div>
      ) : (
        <>
          <div className={`users-entities-crud-content ${readOnly ? 'users-entities-crud-content-readonly' : ''}`}>
            <section className='users-entities-section'>
              <h3 className='users-entities-section-title'>Datos generales</h3>

              <div className='users-entities-grid-row users-entities-grid-row-5'>
                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={form.documentType}
                    label='Tipo de documento *'
                    optionLabel='name'
                    getItems={getUserDocumentTypes}
                    setValue={(value) => {
                      updateField('documentType', value ?? defaultComboDataDTO)
                      setErrors((current) => ({ ...current, documentType: undefined }))
                    }}
                    error={Boolean(errors.documentType)}
                    disable={readOnly}
                  />
                  {errors.documentType && <small className='p-error'>{errors.documentType}</small>}
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    name='usersDocument'
                    label='N° de documento *'
                    value={form.document}
                    onChange={(e) => {
                      updateField('document', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, document: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.document)}
                    caption={errors.document}
                    disable={readOnly}
                  />
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='Nombre *'
                    value={form.firstname}
                    onChange={(e) => {
                      updateField('firstname', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, firstname: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.firstname)}
                    caption={errors.firstname}
                    disable={readOnly}
                  />
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='Apellido *'
                    value={form.lastname}
                    onChange={(e) => {
                      updateField('lastname', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, lastname: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.lastname)}
                    caption={errors.lastname}
                    disable={readOnly}
                  />
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='ID Externo'
                    value={form.externalId}
                    onChange={(e) => updateField('externalId', e?.target?.value ?? '')}
                    inputType={EInputTypes.TEXT}
                    disable={readOnly}
                  />
                </div>
              </div>

              <div className='users-entities-grid-row users-entities-grid-row-4'>
                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={genderOptions.find((option) => option.id === form.gender) ?? null}
                    label='Sexo *'
                    optionLabel='name'
                    getItems={() => Promise.resolve(genderOptions as any)}
                    setValue={(value: GenderOption | null) => {
                      updateField('gender', value?.id ?? 'M')
                      setErrors((current) => ({ ...current, gender: undefined }))
                    }}
                    error={Boolean(errors.gender)}
                    disable={readOnly}
                  />
                  {errors.gender && <small className='p-error'>{errors.gender}</small>}
                </div>

                <div className='users-entities-field'>
                  <InputDate
                    key={`users-birthdate-${form.medereEntityId ?? 'new'}-${form.birthDate instanceof Date ? form.birthDate.getTime() : 'empty'}`}
                    className='w-full'
                    label='Fecha de nacimiento'
                    value={normalizeDateValue(form.birthDate)}
                    onChange={(e) => {
                      updateField('birthDate', normalizeDateValue(e.value))
                      setErrors((current) => ({ ...current, birthDate: undefined }))
                    }}
                    maxDate={new Date()}
                    error={Boolean(errors.birthDate)}
                    caption={errors.birthDate ?? ''}
                    disable={readOnly}
                    style={{ marginBottom: '6px' }}
                  />
                </div>

                <div className='users-entities-field'>
                  <InputPhone
                    className='w-full'
                    cboClassname='users-entities-phone-prefix'
                    label='Celular'
                    name='mobilephone'
                    value={mobilePhone}
                    disable={readOnly}
                    setValue={(phoneNumber: PhoneNumber) => {
                      setMobilePhoneState(phoneNumber)
                      updateField('mobilePhone', mobilePhoneToString(phoneNumber).trim())
                    }}
                    style={{ marginBottom: '8px' }}
                  />
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='Mail'
                    value={form.email}
                    onChange={(e) => {
                      updateField('email', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, email: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.email)}
                    caption={errors.email}
                    disable={readOnly}
                  />
                </div>
              </div>

              <div className='users-entities-grid-row users-entities-grid-row-4'>
                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={form.country}
                    label='País'
                    optionLabel='name'
                    getItems={getUserCountries}
                    setValue={(value) => { void handleCountryChange(value) }}
                    disable={readOnly}
                  />
                </div>

                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={form.province}
                    label='Provincia'
                    optionLabel='name'
                    getItems={getUserProvinces}
                    params={{ countryId: form.country?.id }}
                    setValue={(value) => { void handleProvinceChange(value) }}
                    disable={readOnly || !form.country?.id}
                  />
                </div>

                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={form.city}
                    label='Ciudad'
                    optionLabel='name'
                    getItems={getUserCitiesByProvince}
                    params={{ provinceId: form.province?.id }}
                    setValue={(value) => { void handleCityChange(value) }}
                    error={Boolean(errors.city)}
                    disable={readOnly || !form.province?.id}
                  />
                  {errors.city && <small className='p-error'>{errors.city}</small>}
                </div>

                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='Dirección'
                    value={form.address}
                    onChange={(e) => {
                      updateField('address', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, address: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.address)}
                    caption={errors.address}
                    disable={readOnly}
                  />
                </div>
              </div>
            </section>

            <section className='users-entities-section'>
              <h3 className='users-entities-section-title'>Acceso al sistema</h3>

              <div className='users-entities-grid-row users-entities-grid-row-access-first'>
                <div className='users-entities-field'>
                  <InputTextCustom
                    className='w-full'
                    label='Usuario *'
                    value={form.username}
                    onChange={(e) => {
                      updateField('username', e?.target?.value ?? '')
                      setErrors((current) => ({ ...current, username: undefined }))
                    }}
                    inputType={EInputTypes.TEXT}
                    error={Boolean(errors.username)}
                    caption={errors.username}
                    disable={readOnly}
                  />
                </div>

                {!readOnly && (
                  <>
                    <div className='users-entities-field'>
                      <InputTextCustom
                        className='w-full'
                        label={isEditMode ? 'Nueva contraseña' : 'Contraseña *'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e?.target?.value ?? '')
                          setErrors((current) => ({ ...current, password: undefined, confirmPassword: undefined }))
                        }}
                        inputType={EInputTypes.PASSWORD}
                        error={Boolean(errors.password)}
                        caption={errors.password}
                      />
                    </div>

                    <div className='users-entities-field'>
                      <InputTextCustom
                        className='w-full'
                        label={isEditMode ? 'Confirmar nueva contraseña' : 'Confirmar contraseña *'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e?.target?.value ?? '')
                          setErrors((current) => ({ ...current, confirmPassword: undefined }))
                        }}
                        inputType={EInputTypes.PASSWORD}
                        error={Boolean(errors.confirmPassword)}
                        caption={errors.confirmPassword}
                      />
                    </div>
                  </>
                )}

                <div className='users-entities-switch-field users-entities-switch-field-compact'>
                  <span className='users-entities-switch-label'>Bloqueado</span>
                  <div className='users-entities-switch-control'>
                    <InputSwitch
                      id='usersBlockedSwitchId'
                      checked={form.blocked}
                      onChange={(e) => updateField('blocked', e.value)}
                      disabled={readOnly}
                    />
                    <span className='users-entities-switch-status'>{form.blocked ? 'Sí' : 'No'}</span>
                  </div>
                </div>
              </div>

              <div className='users-entities-grid-row users-entities-grid-row-access-second'>
                <div className='users-entities-field'>
                  <Combobox
                    className='w-full'
                    value={selectedProfile ?? form.profile}
                    label='Perfil *'
                    optionLabel='name'
                    getItems={getUserProfiles}
                    setValue={(value) => {
                      const nextProfile = value ?? defaultComboDataDTO
                      updateField('profile', nextProfile)
                      setErrors((current) => ({ ...current, profile: undefined, healthEntities: undefined }))
                      if (!hasAuditorTag(nextProfile)) {
                        setSelectedHealthEntities([])
                        setForm((current) => ({ ...current, healthEntities: [] }))
                      }
                    }}
                    error={Boolean(errors.profile)}
                    disable={readOnly}
                    style={{ margin: '0px' }}
                  />
                  {errors.profile && <small className='p-error'>{errors.profile}</small>}
                </div>

                <div className='users-entities-signature-field'>
                  <span className='users-entities-signature-label'>Foto de Firma</span>
                  <input
                    id='usersSignatureInput'
                    type='file'
                    accept='image/*'
                    style={{ display: 'none' }}
                    onChange={(event) => { void handleSignatureUpload(event.target.files?.[0]) }}
                  />
                  {loadingSignature ? (
                    <div className='users-entities-signature-loading'>Cargando firma...</div>
                  ) : signaturePreview ? (
                    <div className='users-entities-signature-preview-row'>
                      <img src={signaturePreview} alt='Firma del usuario' className='users-entities-signature-preview' />
                      {!readOnly && (
                        <Button
                          type='button'
                          className='p-button-text users-entities-signature-remove-btn'
                          icon='pi pi-trash'
                          onClick={handleRemoveSignature}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      className={`users-entities-signature-upload ${!readOnly ? 'users-entities-signature-upload-clickable' : ''}`}
                      onClick={() => {
                        if (!readOnly && !loadingSignature) {
                          document.getElementById('usersSignatureInput')?.click()
                        }
                      }}
                    >
                      <button
                        type='button'
                        className='users-entities-signature-upload-button'
                        disabled={readOnly || loadingSignature}
                      >
                        Cargar foto
                      </button>
                    </div>
                  )}
                </div>

              </div>

             
            </section>

            {isAuditorProfile && (
              <section className='users-entities-section'>
                <h3 className='users-entities-section-title'>Entidades</h3>
                <div className='users-entities-health-entity-editor'>
                  <div className='users-entities-health-entity-card'>
                    <div className='users-entities-health-entity-card-header'>
                      <div className='users-entities-health-entity-card-title'>Cobertura médica</div>
                    </div>
                  {!readOnly && (
                    <div className='users-entities-health-entity-controls'>
                      <div className='users-entities-field users-entities-health-entity-combo'>
                        <MultiSelect
                          className='w-full users-entities-health-entity-multiselect'
                          value={selectedHealthEntities}
                          optionLabel='name'
                          options={availableHealthEntityOptions}
                          display='chip'
                          placeholder='Seleccionar'
                          onChange={(e: MultiSelectChangeEvent) => {
                            setSelectedHealthEntities((e.value ?? []) as UsersHealthEntityOptionDTO[])
                          }}
                        />
                      </div>
                      <div className='users-entities-health-entity-add-wrap'>
                        <Button
                          type='button'
                          className='buttonMain2 users-entities-health-entity-add-btn'
                          label='Agregar'
                          onClick={addHealthEntity}
                          disabled={selectedHealthEntities.length === 0}
                        />
                      </div>
                    </div>
                  )}

                  <div className={`users-entities-entity-list ${errors.healthEntities ? 'users-entities-entity-list-error' : ''}`}>
                    {form.healthEntities.length > 0 ? (
                      form.healthEntities.map((item, index) => (
                        <div key={`${item.id}-${item.name}`} className='users-entities-entity-row'>
                          <span className='users-entities-entity-row-label'>{item.name}</span>
                          {!readOnly && (
                            <Button
                              type='button'
                              className='p-button-text users-entities-entity-row-remove'
                              icon='pi pi-times'
                              onClick={() => removeHealthEntity(item.id)}
                            />
                          )}
                          {index < form.healthEntities.length - 1 && <div className='users-entities-entity-row-divider' />}
                        </div>
                      ))
                    ) : (
                      <span className='users-entities-entity-empty'>No hay coberturas médicas agregadas.</span>
                    )}
                  </div>
                  </div>
                  {errors.healthEntities && <small className='p-error'>{errors.healthEntities}</small>}
                </div>
              </section>
            )}
          </div>

          {!readOnly && (
            <div className='users-entities-crud-actions'>
              <Button
                className='buttonMain2 users-entities-action-btn'
                label='Cancelar'
                onClick={() => setUserId(null)}
                disabled={saving}
              />
              <Button
                className='buttonMain3 users-entities-action-btn users-entities-primary-btn'
                label={saving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Crear usuario'}
                onClick={() => { void handleSave() }}
                disabled={saving}
              />
            </div>
          )}

          <Modal
            visible={conversionModalVisible}
            setVisible={(visible) => {
              if (!visible) {
                closeConversionModal()
                return
              }
              setConversionModalVisible(true)
            }}
            className='users-conversion-dialog'
            header={<p className='users-conversion-modal-title'>Se encontró una persona con el número de documento ingresado</p>}
            footerButtonLeftText='Cancelar'
            onClickLeftBtn={closeConversionModal}
            footerButtonRightText={selectedConversionCandidate?.hasUser ? 'Editar usuario existente' : undefined}
            onClickRightBtn={() => {
              if (selectedConversionCandidate) {
                applyConversionCandidate(selectedConversionCandidate)
              }
            }}
            rightButtonDisabled={!selectedConversionCandidate?.hasUser}
            extraButtons={(
              <Button
                type='button'
                style={{ order: 4 }}
                className='buttonMain2'
                label={conversionCreateButtonLabel}
                disabled={!selectedConversionCandidate}
                onClick={() => {
                  if (selectedConversionCandidate) {
                    applyConversionCandidateAsNewProfile(selectedConversionCandidate)
                  }
                }}
              />
            )}
            width='w-2xlarge'
          >
            <div className='users-conversion-modal-content'>
              <p className='users-conversion-modal-subtitle'>
                {conversionModalSubtitle}
              </p>

              <p className='users-conversion-modal-results-title'>
                Resultados de tu <strong>búsqueda</strong>
              </p>

              <div className='users-conversion-candidates-list'>
                {conversionCandidates.map((candidate) => (
                  <button
                    type='button'
                    key={`${candidate.medereEntityId}-${candidate.userId ?? 'new'}`}
                    className={`users-conversion-candidate-card ${selectedConversionCandidateId === candidate.medereEntityId ? 'selected' : ''}`}
                    onClick={() => setSelectedConversionCandidateId(candidate.medereEntityId)}
                  >
                      <RadioButton
                        inputId={`users-conversion-candidate-${candidate.medereEntityId}`}
                        name='users-conversion-candidate'
                        value={candidate.medereEntityId}
                        checked={selectedConversionCandidateId === candidate.medereEntityId}
                        onChange={() => setSelectedConversionCandidateId(candidate.medereEntityId)}
                      />
                      <div className='users-conversion-candidate-content'>
                        <div className='users-conversion-candidate-header-row'>
                          <p className='users-conversion-candidate-name'>
                            {candidate.firstname} {candidate.lastname}
                          </p>
                          <Chip
                            label={getConversionCandidateTypeLabel(candidate)}
                            className='users-conversion-candidate-chip'
                          />
                        </div>
                        <p className='users-conversion-candidate-doc'>
                          {formatConversionDocument(candidate)}
                        </p>
                        {candidate.username && (
                          <p className='users-conversion-candidate-doc'>
                            <strong>Usuario:</strong> {candidate.username}
                          </p>
                        )}
                      </div>
                    </button>
                ))}
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  )
}
